import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { transactionAPI } from '../api/client';
import { Loader2, ChevronDown, ChevronLeft, ChevronRight, Upload, X, Plus, Filter, Search } from 'lucide-react';
import { CATEGORIES } from '../constants/categories';
import { formatAmount as formatCurrencyAmount } from '../utils/currency';
import { useAnalytics } from '../hooks/useAnalytics';
import TransactionEditModal from './TransactionEditModal';
import AddExpense from './AddExpense';
import UploadPanel from './UploadPanel';
import { useTokens } from '../context/ThemeContext';


function ExploreView({ baseCurrency = 'INR', customCategories = [], excludedCategories = [] }) {
  const t = useTokens();
  const styles = makeStyles(t);
  const { track } = useAnalytics();
  const location = useLocation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUploadPanel, setShowUploadPanel] = useState(false);

  // Transaction edit modal
  const [editingTransaction, setEditingTransaction] = useState(null);
  // Manual add expense modal
  const [showAddExpense, setShowAddExpense] = useState(false);
  // Add menu (add expense vs upload statement)
  const [showAddMenu, setShowAddMenu] = useState(false);
  // Search — pre-populate from navigation state (e.g. clicking a merchant in Analytics)
  const [searchQuery, setSearchQuery] = useState(location.state?.searchQuery || '');
  const [showSearch, setShowSearch] = useState(!!location.state?.searchQuery);
  const searchInputRef = useRef(null);
  // Category filter (applies to active view)
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Bank filter
  const [availableBanks, setAvailableBanks] = useState([]);
  const [bankFilter, setBankFilter] = useState(null);

  // View mode: 'yearly', 'quarterly', 'monthly', 'weekly', 'daily', 'custom'
  const [viewMode, setViewMode] = useState('monthly');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const now = new Date();

  // Use local date parts to avoid UTC offset shifting the date
  const toLocalStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedYearView, setSelectedYearView] = useState(now.getFullYear());
  const [selectedQuarterYear, setSelectedQuarterYear] = useState(now.getFullYear());
  const [selectedQuarterIndex, setSelectedQuarterIndex] = useState(Math.floor(now.getMonth() / 3));
  const [weekStartStr, setWeekStartStr] = useState(() => {
    const d = new Date(now);
    d.setDate(d.getDate() - d.getDay());
    return toLocalStr(d);
  });
  const [dailyDateStr, setDailyDateStr] = useState(toLocalStr(now));

  // Custom date range
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    return toLocalStr(d);
  });
  const [customEnd, setCustomEnd] = useState(() => toLocalStr(now));


  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const today = new Date(); today.setHours(0, 0, 0, 0);

  const canGoNext = () => {
    if (viewMode === 'daily') return dailyDateStr < toLocalStr(today);
    if (viewMode === 'weekly') {
      const next = new Date(weekStartStr); next.setDate(next.getDate() + 7);
      return toLocalStr(next) <= toLocalStr(today);
    }
    if (viewMode === 'monthly') return selectedYear < now.getFullYear() || selectedMonth < now.getMonth();
    if (viewMode === 'quarterly') return selectedQuarterYear < now.getFullYear() || selectedQuarterIndex < Math.floor(now.getMonth() / 3);
    if (viewMode === 'yearly') return selectedYearView < now.getFullYear();
    return false;
  };

  useEffect(() => {
    transactionAPI.getBanks().then(res => setAvailableBanks(res.data.banks || [])).catch(() => {});
  }, []);

  const navigate = (dir) => {
    if (viewMode === 'daily') {
      const d = new Date(dailyDateStr); d.setDate(d.getDate() + dir);
      setDailyDateStr(toLocalStr(d));
    } else if (viewMode === 'weekly') {
      const d = new Date(weekStartStr); d.setDate(d.getDate() + dir * 7);
      setWeekStartStr(toLocalStr(d));
    } else if (viewMode === 'monthly') {
      let m = selectedMonth + dir, y = selectedYear;
      if (m < 0) { m = 11; y -= 1; }
      if (m > 11) { m = 0; y += 1; }
      setSelectedMonth(m); setSelectedYear(y);
    } else if (viewMode === 'quarterly') {
      let q = selectedQuarterIndex + dir, y = selectedQuarterYear;
      if (q < 0) { q = 3; y -= 1; }
      if (q > 3) { q = 0; y += 1; }
      setSelectedQuarterIndex(q); setSelectedQuarterYear(y);
    } else if (viewMode === 'yearly') {
      setSelectedYearView(prev => prev + dir);
    }
  };

  const excludedKey = excludedCategories.join(',');
  const categoryKey = categoryFilter.join(',');

  const fetchSummaryRef = useRef(null);

  useEffect(() => {
    const toStr = toLocalStr;
    let params;
    switch (viewMode) {
      case 'yearly': {
        const start = new Date(selectedYearView, 0, 1);
        const end = new Date(selectedYearView, 11, 31);
        params = { startDate: toStr(start), endDate: toStr(end), groupBy: 'month' };
        break;
      }
      case 'quarterly': {
        const startMonth = selectedQuarterIndex * 3;
        const start = new Date(selectedQuarterYear, startMonth, 1);
        const end = new Date(selectedQuarterYear, startMonth + 3, 0);
        params = { startDate: toStr(start), endDate: toStr(end), groupBy: 'month' };
        break;
      }
      case 'monthly': {
        const start = new Date(selectedYear, selectedMonth, 1);
        const end = new Date(selectedYear, selectedMonth + 1, 0);
        params = { startDate: toStr(start), endDate: toStr(end), groupBy: 'week' };
        break;
      }
      case 'weekly': {
        const start = new Date(weekStartStr);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        params = { startDate: weekStartStr, endDate: toStr(end), groupBy: 'day' };
        break;
      }
      case 'daily': {
        params = { startDate: dailyDateStr, endDate: dailyDateStr, groupBy: 'hour' };
        break;
      }
      case 'custom': {
        if (!customStart || !customEnd) return;
        params = { startDate: customStart, endDate: customEnd, groupBy: 'month' };
        break;
      }
      default: return;
    }

    let cancelled = false;
    setLoading(true);
    transactionAPI.getSummary({
      ...params,
      excludedCategories: excludedKey,
      ...(categoryKey ? { categories: categoryKey } : {}),
    }).then(res => {
      if (!cancelled) setSummary(res.data);
    }).catch(err => {
      console.error('Failed to load summary:', err);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    fetchSummaryRef.current = () => {
      setLoading(true);
      transactionAPI.getSummary({
        ...params,
        excludedCategories: excludedKey,
        ...(categoryKey ? { categories: categoryKey } : {}),
      }).then(res => setSummary(res.data))
        .catch(err => console.error('Failed to load summary:', err))
        .finally(() => setLoading(false));
    };

    return () => { cancelled = true; };
  }, [viewMode, selectedYearView, selectedQuarterYear, selectedQuarterIndex, selectedYear, selectedMonth, weekStartStr, dailyDateStr, customStart, customEnd, excludedKey, categoryKey]);

  const refetchSummary = () => fetchSummaryRef.current?.();

  const handleTransactionSaved = () => { refetchSummary(); };
  const handleTransactionDeleted = () => { setEditingTransaction(null); refetchSummary(); };

  const isTodaySelected = () => {
    const today = new Date();
    return new Date(dailyDateStr).toDateString() === today.toDateString();
  };

  // Quarter helpers
  const getQuarterLabel = (year, quarterIndex) => {
    const labels = ['Q1', 'Q2', 'Q3', 'Q4'];
    return `${labels[quarterIndex]} ${year}`;
  };

  const formatDailyDate = () => {
    if (isTodaySelected()) {
      return 'Today';
    }
    return new Date(dailyDateStr).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  // Unpack summary data
  const transactions = summary?.transactions || [];


  // For per-item display: return the converted amount in baseCurrency, or null if unavailable.
  const getDisplayConverted = (expense) => {
    const txCurrency = (expense.currency || 'INR').toUpperCase();
    const base = baseCurrency.toUpperCase();
    if (txCurrency === base) return null;
    if (expense.convertedAmount != null && (expense.baseCurrency || '').toUpperCase() === base) {
      return parseFloat(expense.convertedAmount);
    }
    return null;
  };

  // The overall period total is the sum of all chartSeries entries
  const periodTotal = summary?.totalSpend ?? 0;

  const formatAmount = (amount, currency) => formatCurrencyAmount(amount, currency);

  const formatWeekRange = () => {
    const weekEnd = new Date(new Date(weekStartStr));
    weekEnd.setDate(weekEnd.getDate() + 6);
    const options = { month: 'short', day: 'numeric' };
    return `${new Date(weekStartStr).toLocaleDateString('en-US', options)} - ${weekEnd.toLocaleDateString('en-US', options)}`;
  };

  const displayYear = selectedYear !== now.getFullYear() ? ` ${selectedYear}` : '';

  const viewModeLabels = {
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
    custom: 'Custom',
  };

  const VIEW_MODES = ['yearly', 'quarterly', 'monthly', 'weekly', 'daily', 'custom'];


  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <Loader2 size={40} style={styles.spinnerIcon} />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Transaction Edit Modal */}
      {editingTransaction && (
        <TransactionEditModal
          transaction={editingTransaction}
          onClose={() => setEditingTransaction(null)}
          onDeleted={handleTransactionDeleted}
          onSaved={handleTransactionSaved}
          customCategories={customCategories}
        />
      )}

      {/* Add Expense Modal */}
      {showAddExpense && (
        <AddExpense
          onClose={() => setShowAddExpense(false)}
          onSuccess={refetchSummary}
          baseCurrency={baseCurrency}
          customCategories={customCategories}
        />
      )}

      {/* Non-blocking upload panel */}
      <UploadPanel onUploadComplete={refetchSummary} openOnMount={showUploadPanel} />


      {/* Controls Row: Upload and Duration Selection */}
      <div style={styles.controlsRow}>
        <div style={styles.leftButtons}>
          <div style={{ position: 'relative' }}>
            <button
              data-tour="add-btn"
              onClick={() => setShowAddMenu(v => !v)}
              style={styles.addButton}
              title="Add"
            >
              <Plus size={16} />
            </button>
            {showAddMenu && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setShowAddMenu(false)} />
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 100, backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', minWidth: '170px' }}>
                  <button
                    onClick={() => { setShowAddMenu(false); setShowAddExpense(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '11px 14px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontSize: '14px', color: t.text, textAlign: 'left' }}
                  >
                    <Plus size={15} color={t.textSecondary} />
                    Add expense
                  </button>
                  <div style={{ height: '1px', backgroundColor: t.border, margin: '0 10px' }} />
                  <button
                    onClick={() => { setShowAddMenu(false); setShowUploadPanel(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '11px 14px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontSize: '14px', color: t.text, textAlign: 'left' }}
                  >
                    <Upload size={15} color={t.textSecondary} />
                    Upload statement
                  </button>
                </div>
              </>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', height: '34px', border: `1px solid ${showSearch ? t.inputBorder : t.border}`, borderRadius: '10px', backgroundColor: t.inputBg, overflow: 'hidden', width: showSearch ? '180px' : '34px', transition: 'width 0.2s ease', boxSizing: 'border-box' }}>
            <button
              data-tour="search-btn"
              onClick={() => { setShowSearch(v => !v); if (!showSearch) setTimeout(() => searchInputRef.current?.focus(), 50); else setSearchQuery(''); }}
              style={{ flexShrink: 0, width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: showSearch ? t.textSlate : t.textSecondary }}
            >
              {showSearch ? <X size={15} /> : <Search size={15} />}
            </button>
            {showSearch && (
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search..."
                style={{ flex: 1, height: '100%', border: 'none', outline: 'none', backgroundColor: 'transparent', fontSize: '13px', color: t.text, paddingRight: '8px', minWidth: 0 }}
              />
            )}
          </div>
        </div>
        <div style={styles.rightControls}>
          {/* Filter button + clear */}
          <div style={styles.filterWrap}>
            <div style={styles.filterPill}>
              <button
                onClick={() => setShowFilterDropdown(v => !v)}
                style={{
                  ...styles.filterButton,
                  ...((categoryFilter.length > 0 || bankFilter) ? styles.filterButtonActive : {}),
                }}
                title="Filter"
              >
                <Filter size={14} />
                {(categoryFilter.length > 0 || bankFilter) && (
                  <span style={styles.filterCount}>{categoryFilter.length + (bankFilter ? 1 : 0)}</span>
                )}
              </button>
              {(categoryFilter.length > 0 || bankFilter) && (
                <button
                  onClick={() => { setCategoryFilter([]); setBankFilter(null); }}
                  style={styles.filterClearBtn}
                  title="Clear all filters"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            {showFilterDropdown && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setShowFilterDropdown(false)} />
                <div style={styles.filterDropdown}>
                  {/* Category section */}
                  <div style={styles.filterSectionHeader}>Category</div>
                  {CATEGORIES.map(cat => {
                    const selected = categoryFilter.includes(cat);
                    return (
                      <button
                        key={cat}
                        style={{ ...styles.filterOption, ...(selected ? styles.filterOptionActive : {}) }}
                        onClick={() => setCategoryFilter(prev =>
                          selected ? prev.filter(c => c !== cat) : [...prev, cat]
                        )}
                      >
                        <span style={{ ...styles.filterCheckbox, ...(selected ? styles.filterCheckboxActive : {}) }}>
                          {selected ? '✓' : ''}
                        </span>
                        {cat}
                      </button>
                    );
                  })}
                  {/* Bank section — only shown when user has data from multiple banks */}
                  {availableBanks.length > 0 && (
                    <>
                      <div style={styles.filterSectionDivider} />
                      <div style={styles.filterSectionHeader}>Bank</div>
                      {availableBanks.map(bank => {
                        const selected = bankFilter === bank;
                        return (
                          <button
                            key={bank}
                            style={{ ...styles.filterOption, ...(selected ? styles.filterOptionActive : {}) }}
                            onClick={() => setBankFilter(selected ? null : bank)}
                          >
                            <span style={{ ...styles.filterCheckbox, ...(selected ? styles.filterCheckboxActive : {}) }}>
                              {selected ? '✓' : ''}
                            </span>
                            {bank}
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          {/* View mode dropdown */}
          <div data-tour="view-mode" style={styles.dropdownContainer}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              style={styles.dropdownButton}
            >
              {viewModeLabels[viewMode]}
              <ChevronDown size={16} />
            </button>
            {dropdownOpen && (
              <div style={styles.dropdownMenu}>
                {VIEW_MODES.map((mode) => (
                  <button
                    key={mode}
                    onClick={() => {
                      track('view_mode_changed', { mode });
                      setViewMode(mode);
                      setDropdownOpen(false);
                    }}
                    style={{
                      ...styles.dropdownItem,
                      ...(viewMode === mode ? styles.dropdownItemActive : {}),
                    }}
                  >
                    {viewModeLabels[mode]}
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Summary Card with Navigation */}
      <div style={styles.summaryCard}>
        {viewMode === 'custom' ? (
          <div style={styles.customRangeRow}>
            <input
              type="date"
              value={customStart}
              max={customEnd}
              onChange={e => setCustomStart(e.target.value)}
              style={styles.dateInput}
            />
            <span style={styles.dateRangeSep}>to</span>
            <input
              type="date"
              value={customEnd}
              min={customStart}
              max={now.toISOString().slice(0, 10)}
              onChange={e => setCustomEnd(e.target.value)}
              style={styles.dateInput}
            />
            <div style={styles.customTotal}>{formatAmount(periodTotal, baseCurrency)}</div>
          </div>
        ) : (
          <div style={{ ...styles.summaryContent, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <button onClick={() => navigate(-1)} style={{ ...styles.navButton }}>
                <ChevronLeft size={18} />
              </button>
              <div style={{ ...styles.summaryLabel, marginBottom: 0, minWidth: '140px', textAlign: 'center' }}>
                {viewMode === 'yearly' && selectedYearView}
                {viewMode === 'quarterly' && getQuarterLabel(selectedQuarterYear, selectedQuarterIndex)}
                {viewMode === 'monthly' && `${monthNames[selectedMonth]}${displayYear}`}
                {viewMode === 'weekly' && `${formatWeekRange()}${new Date(weekStartStr).getFullYear() !== now.getFullYear() ? ` ${new Date(weekStartStr).getFullYear()}` : ''}`}
                {viewMode === 'daily' && formatDailyDate()}
              </div>
              <button onClick={() => navigate(1)} disabled={!canGoNext()} style={{ ...styles.navButton, ...(canGoNext() ? {} : styles.navButtonDisabled) }}>
                <ChevronRight size={18} />
              </button>
            </div>
            <div style={styles.summaryValue}>{formatAmount(periodTotal, baseCurrency)}</div>
          </div>
        )}
      </div>

      {/* Flat transaction list */}
      {(() => {
        const q = searchQuery.trim().toLowerCase();
        const allTxns = transactions.filter(e => {
          if (categoryFilter.length > 0 && !categoryFilter.includes(e.category)) return false;
          if (q) {
            const name = (e.receiver || e.merchant || e.category || '').toLowerCase();
            const cat = (e.category || '').toLowerCase();
            if (!name.includes(q) && !cat.includes(q)) return false;
          }
          return true;
        });
        const sorted = [...allTxns].sort((a, b) => new Date(b.transactionDate) - new Date(a.transactionDate));
        if (sorted.length === 0) return <p style={styles.emptyText}>No transactions in this period.</p>;
        return (
          <div style={styles.detailsCard}>
            <div style={styles.expensesList}>
              {sorted.map(expense => {
                const isCredit = expense.transactionType === 'credit';
                const displayConverted = getDisplayConverted(expense);
                return (
                  <div key={expense._id || expense.id} style={{ ...styles.expenseItem, cursor: 'pointer' }} onClick={() => setEditingTransaction(expense)}>
                    <div style={styles.expenseMain}>
                      <div style={styles.expenseInfo}>
                        <div style={styles.expenseMerchant}>{expense.receiver || expense.merchant || expense.category}</div>
                        <div style={styles.expenseCategory}>
                          {expense.category}
                          {expense.bank && ` • ${expense.bank}`}
                          {' • '}{new Date(expense.transactionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ ...styles.expenseAmount, color: isCredit ? t.success : t.text }}>
                          {isCredit ? '+' : '-'}{formatAmount(expense.amount, expense.currency)}
                        </div>
                        {displayConverted != null && (
                          <div style={styles.convertedAmount}>≈ {isCredit ? '+' : '-'}{formatAmount(displayConverted, baseCurrency)}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function makeStyles(t) { return {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    gap: '16px',
    color: t.textSecondary,
  },
  spinnerIcon: {
    animation: 'spin 1s linear infinite',
  },
  spinningIcon: {
    animation: 'spin 1s linear infinite',
  },
  // Controls Row
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  leftButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  rightControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.itemSelected,
    color: 'white',
    border: 'none',
    width: '34px',
    height: '34px',
    borderRadius: '10px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: t.surface,
    borderRadius: '16px',
    width: '90%',
    maxWidth: '400px',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${t.border}`,
  },
  modalTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: t.text,
    margin: 0,
  },
  modalClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: t.textSecondary,
    padding: '4px',
  },
  modalBody: {
    padding: '20px',
  },
  uploadArea: {
    border: `2px dashed ${t.border}`,
    borderRadius: '12px',
    padding: '32px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  fileInput: {
    display: 'none',
  },
  uploadLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
  },
  uploadText: {
    fontSize: '14px',
    color: t.textSlate,
    fontWeight: '500',
  },
  uploadHint: {
    fontSize: '12px',
    color: t.textMuted,
  },
  uploadSuccess: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: t.successBg,
    color: t.success,
    borderRadius: '8px',
    fontSize: '13px',
    textAlign: 'center',
  },
  uploadError: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: t.dangerBg,
    color: t.danger,
    borderRadius: '8px',
    fontSize: '13px',
    textAlign: 'center',
  },
  passwordPrompt: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '8px 0',
  },
  passwordPromptText: {
    fontSize: '14px',
    color: t.textSlate,
    textAlign: 'center',
    margin: 0,
  },
  passwordInputRow: {
    width: '100%',
  },
  passwordInput: {
    width: '100%',
    padding: '10px 14px',
    border: `1px solid ${t.inputBorder}`,
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: t.inputBg,
    color: t.text,
  },
  passwordActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    width: '100%',
  },
  passwordCancelBtn: {
    padding: '8px 16px',
    border: `1px solid ${t.border}`,
    borderRadius: '8px',
    background: t.surface,
    color: t.textSlate,
    fontSize: '14px',
    cursor: 'pointer',
  },
  passwordSubmitBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    border: 'none',
    borderRadius: '8px',
    background: t.textSlate,
    color: 'white',
    fontSize: '14px',
    cursor: 'pointer',
  },
  dropdownContainer: {
    position: 'relative',
  },
  dropdownButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: t.surface,
    color: t.textSlate,
    border: `1px solid ${t.border}`,
    padding: '8px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    backgroundColor: t.surface,
    border: `1px solid ${t.border}`,
    borderRadius: '10px',
    boxShadow: t.dropdownShadow,
    overflow: 'hidden',
    zIndex: 100,
    minWidth: '140px',
    whiteSpace: 'nowrap',
  },
  dropdownItem: {
    display: 'block',
    width: '100%',
    padding: '10px 20px',
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'left',
    fontSize: '13px',
    color: t.textSlate,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  dropdownItemActive: {
    backgroundColor: t.chipBg,
    fontWeight: '600',
  },
  navArrows: {
    display: 'flex',
    gap: '2px',
  },
  navBtn: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${t.border}`,
    borderRadius: '8px',
    backgroundColor: t.surface,
    color: t.textSlate,
    cursor: 'pointer',
    fontSize: '18px',
    lineHeight: 1,
  },
  navBtnDisabled: {
    opacity: 0.35,
    cursor: 'default',
  },
  summaryCard: {
    backgroundColor: t.surface,
    borderRadius: '16px',
    padding: '24px',
    boxShadow: t.cardShadow,
    border: `1px solid ${t.border}`,
  },
  navRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: t.chipBg,
    cursor: 'pointer',
    color: t.textSlate,
    transition: 'all 0.2s',
  },
  navButtonDisabled: {
    opacity: 0.3,
    cursor: 'not-allowed',
  },
  customRangeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    padding: '4px 0',
  },
  dateInput: {
    flex: 1,
    minWidth: '130px',
    padding: '8px 10px',
    border: `1px solid ${t.inputBorder}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: t.textSlate,
    outline: 'none',
    backgroundColor: t.inputBg,
  },
  dateRangeSep: {
    fontSize: '13px',
    color: t.textMuted,
    flexShrink: 0,
  },
  customTotal: {
    fontSize: '18px',
    fontWeight: '500',
    color: t.text,
    flexShrink: 0,
    marginLeft: 'auto',
  },
  summaryContent: {
    textAlign: 'center',
    flex: 1,
  },
  summaryLabel: {
    fontSize: '14px',
    color: t.textSecondary,
    marginBottom: '8px',
  },
  summaryValue: {
    fontSize: '36px',
    fontWeight: '300',
    color: t.text,
  },
  selectorCard: {
    backgroundColor: t.surface,
    borderRadius: '16px',
    padding: '16px',
    boxShadow: t.cardShadow,
    border: `1px solid ${t.border}`,
  },
  itemsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
    gap: '8px',
  },
  itemButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    padding: '16px 8px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative',
  },
  itemButtonSelected: {
    backgroundColor: t.itemSelected,
  },
  itemButtonFuture: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  itemLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: t.text,
  },
  itemLabelSelected: {
    color: t.itemSelectedText,
  },
  itemAmount: {
    fontSize: '12px',
    color: t.textSecondary,
  },
  itemAmountSelected: {
    color: t.itemSelectedText,
    opacity: 0.7,
  },
  itemAmountZero: {
    color: t.textMuted,
  },
  currentDot: {
    position: 'absolute',
    bottom: '6px',
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '8px',
  },
  dayButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '12px 4px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative',
  },
  dayButtonSelected: {
    backgroundColor: t.itemSelected,
  },
  dayButtonFuture: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  dayName: {
    fontSize: '12px',
    fontWeight: '500',
    color: t.textSecondary,
  },
  dayNameSelected: {
    color: t.itemSelectedText,
  },
  dayDate: {
    fontSize: '18px',
    fontWeight: '500',
    color: t.text,
  },
  dayDateSelected: {
    color: t.itemSelectedText,
  },
  dayAmount: {
    fontSize: '11px',
    color: t.textSecondary,
  },
  dayAmountSelected: {
    color: t.itemSelectedText,
    opacity: 0.7,
  },
  dayAmountZero: {
    color: t.textMuted,
  },
  todayDot: {
    position: 'absolute',
    bottom: '4px',
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
  },
  detailsCard: {
    backgroundColor: t.surface,
    borderRadius: '16px',
    padding: '24px',
    boxShadow: t.cardShadow,
    border: `1px solid ${t.border}`,
  },
  detailsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${t.borderLight}`,
  },
  detailsTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: t.text,
  },
  detailsSubtitle: {
    fontSize: '13px',
    color: t.textSecondary,
    marginTop: '4px',
  },
  detailsTotal: {
    fontSize: '20px',
    fontWeight: '500',
    color: t.text,
  },
  expensesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  expenseItem: {
    paddingBottom: '16px',
    borderBottom: `1px solid ${t.borderLight}`,
  },
  expenseMain: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  expenseInfo: {
    flex: 1,
  },
  expenseMerchant: {
    fontSize: '14px',
    fontWeight: '500',
    color: t.text,
    marginBottom: '4px',
  },
  expenseCategory: {
    fontSize: '12px',
    color: t.textSecondary,
  },
  expenseAmount: {
    fontSize: '14px',
    fontWeight: '500',
    color: t.text,
  },
  convertedAmount: {
    fontSize: '11px',
    color: t.textMuted,
    marginTop: '2px',
  },
  emptyText: {
    fontSize: '14px',
    color: t.textSecondary,
    textAlign: 'center',
    padding: '24px 0',
  },
  filterWrap: {
    position: 'relative',
  },
  filterPill: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${t.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: t.surface,
  },
  filterButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    height: '32px',
    padding: '0 10px',
    border: 'none',
    backgroundColor: 'transparent',
    color: t.textSecondary,
    cursor: 'pointer',
  },
  filterButtonActive: {
    backgroundColor: t.itemSelected,
    color: t.itemSelectedText,
  },
  filterCount: {
    fontSize: '11px',
    fontWeight: '700',
    lineHeight: 1,
  },
  filterClearBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '32px',
    width: '28px',
    border: 'none',
    borderLeft: `1px solid ${t.border}`,
    backgroundColor: t.itemSelected,
    color: t.itemSelectedText,
    cursor: 'pointer',
    padding: 0,
  },
  filterDropdown: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    backgroundColor: t.surface,
    border: `1px solid ${t.border}`,
    borderRadius: '10px',
    boxShadow: t.dropdownShadow,
    zIndex: 200,
    minWidth: '180px',
    maxHeight: '260px',
    overflowY: 'auto',
    padding: '4px 0',
  },
  filterDivider: {
    height: '1px',
    backgroundColor: t.border,
    margin: '4px 0',
  },
  filterOption: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '7px 14px',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '13px',
    color: t.textSlate,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  },
  filterOptionActive: {
    color: t.text,
    fontWeight: '500',
  },
  filterCheckbox: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    marginRight: '10px',
    fontSize: '10px',
    border: `1.5px solid ${t.inputBorder}`,
    borderRadius: '4px',
    flexShrink: 0,
    color: 'transparent',
    backgroundColor: t.inputBg,
  },
  filterCheckboxActive: {
    backgroundColor: '#2563eb',
    borderColor: '#2563eb',
    color: 'white',
  },
  filterSectionHeader: {
    padding: '6px 14px 4px',
    fontSize: '11px',
    fontWeight: '600',
    color: t.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  filterSectionDivider: {
    height: '1px',
    backgroundColor: t.border,
    margin: '4px 0',
  },
  activeFilter: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: t.chipBg,
    borderRadius: '20px',
    padding: '4px 10px 4px 12px',
    fontSize: '12px',
    color: t.chipText,
    marginBottom: '12px',
  },
  clearFilter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: t.textSecondary,
    padding: 0,
  },
}; }

export default ExploreView;
